import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { ropaAPI, exportAPI, tasksAPI } from '../../../services/api';
import { TasksCountBadge } from '../../../components/shared/TasksCountBadge';
import { useAuth } from '../../../contexts/AuthContext';
import { useGlobalContext } from '../../../contexts/GlobalContext';
import {
  Plus, Search, Edit, Trash2, MoreHorizontal, Eye,
  Download, FileText, FileSpreadsheet, ClipboardList,
  Clock, CheckCircle, Archive, FileCheck, FileDown,
  AlertTriangle, AlertCircle, FileX, ChevronRight,
  Shield, ShieldCheck, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { PERSONAL_DATA_CATEGORIES, SENSITIVE_DATA_CATEGORIES } from '../../../components/ui/category-accordion-selector';
import { CSVImportDialog } from './components/CSVImportDialog';
import { CreateTempGuestDialog } from '../../../components/guest/CreateTempGuestDialog';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:          { label: 'Draft',          color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',           icon: FileText   },
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',     icon: Clock      },
  approved:       { label: 'Approved',       color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',         icon: FileCheck  },
  active:         { label: 'Active',         color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',     icon: CheckCircle},
  archived:       { label: 'Archived',       color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',        icon: Archive    },
};

const LEGAL_BASIS_LABELS = {
  consent:              'Consent',
  contract:             'Contract',
  legal_obligation:     'Legal Obligation',
  vital_interests:      'Vital Interests',
  public_task:          'Public Task',
  legitimate_interests: 'Legitimate Interests',
};

// Legal basis bar colours (dark-mode safe Tailwind bg classes)
const LEGAL_BASIS_COLORS = {
  contract:             'bg-emerald-500',
  legal_obligation:     'bg-blue-500',
  consent:              'bg-purple-500',
  legitimate_interests: 'bg-amber-500',
  vital_interests:      'bg-orange-500',
  public_task:          'bg-slate-400',
};

const SENSITIVE_BASIS_LABELS = {
  explicit_consent:  'Explicit consent',
  employment_law:    'Empl. / Social Security Law',
  vital_interests:   'Vital interests',
  health_purposes:   'Health purposes',
  public_health:     'Public health',
  legal_claims:      'Legal claims',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getCategoryNames = (items, categories) => {
  const names = new Set();
  (items || []).forEach(item => {
    categories.forEach(cat => {
      if (cat.items.includes(item)) names.add(cat.category);
    });
  });
  return Array.from(names);
};

// Flatten array fields across all records and count occurrences
const flatCount = (records, field) => {
  const counts = {};
  records.forEach(r => {
    (r[field] || []).forEach(v => {
      if (v) counts[v] = (counts[v] || 0) + 1;
    });
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

// Generic horizontal bar chart row
function BarRow({ label, count, max, color = 'bg-emerald-500', onClick }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div
      className={cn('flex items-center gap-3 group', onClick && 'cursor-pointer')}
      onClick={onClick}
    >
      <span className={cn(
        'text-sm w-40 shrink-0 truncate text-slate-700 dark:text-slate-300',
        onClick && 'group-hover:text-primary transition-colors'
      )}>
        {label}
      </span>
      <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm font-medium w-6 text-right text-slate-600 dark:text-slate-400 shrink-0">
        {count}
      </span>
    </div>
  );
}

// Chip tag
function Chip({ label, count, color = 'teal' }) {
  const cls = {
    teal: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800',
    red:  'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
    blue: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
  }[color] || 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';

  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border', cls)}>
      {label}
      <span className="font-bold">{count}</span>
    </span>
  );
}

// Panel wrapper
function Panel({ title, children, className }) {
  return (
    <Card className={className}>
      <CardHeader className="pb-3 pt-4 px-5">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {children}
      </CardContent>
    </Card>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function ROPAPage() {
  const navigate   = useNavigate();
  const { user, isDPO, isClientAdmin, isEntityAdmin, isComplianceReadOnly: isReadOnly, isViewer } = useAuth();
  const { selectedClientId, selectedEntityId, selectedDepartmentId, entities } = useGlobalContext();

  const [records,             setRecords]             = useState([]);
  const [taskCounts,          setTaskCounts]          = useState({});
  const [loading,             setLoading]             = useState(true);
  const [searchTerm,          setSearchTerm]          = useState('');
  const [selectedStatus,      setSelectedStatus]      = useState('');
  const [activeStatCard,      setActiveStatCard]      = useState('total');
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [intakeDialogOpen,    setIntakeDialogOpen]    = useState(false);
  const [view,                setView]                = useState('dashboard');
  const [filterDept,          setFilterDept]          = useState(null);
  const [attentionIds,        setAttentionIds]        = useState(null); // set of IDs from attention row click

  // ── Data fetch ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [res, countsRes] = await Promise.all([
        ropaAPI.getAll(selectedEntityId || undefined, selectedClientId || undefined, selectedDepartmentId || undefined),
        tasksAPI.getCountsBatch('ropa').catch(() => ({ data: {} })),
      ]);
      setRecords(res.data);
      setTaskCounts(countsRes.data || {});
    } catch {
      toast.error('Failed to fetch ROPA records');
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, selectedClientId, selectedDepartmentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this processing activity?')) return;
    try {
      await ropaAPI.delete(id);
      toast.success('Processing activity deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete record');
    }
  };

  // ── Derived stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:          records.length,
    active:         records.filter(r => r.status === 'active').length,
    draft:          records.filter(r => r.status === 'draft').length,
    pending_review: records.filter(r => r.status === 'pending_review').length,
    approved:       records.filter(r => r.status === 'approved').length,
    archived:       records.filter(r => r.status === 'archived').length,
    needs_attention: records.filter(r => r.status === 'draft' || r.status === 'pending_review').length,
    sensitive:      records.filter(r => r.collects_sensitive_data).length,
  }), [records]);

  // ── Attention panel conditions ──────────────────────────────────────────────
  const attention = useMemo(() => {
    const items = [];
    const noSensitiveBasis = records.filter(r =>
      r.collects_sensitive_data &&
      (!r.sensitive_data_legal_basis || r.sensitive_data_legal_basis === '')
    );
    if (noSensitiveBasis.length)
      items.push({ id: 'no_sensitive_basis', severity: 'red', icon: AlertTriangle,
        title: 'Sensitive data — no legal basis recorded',
        hint:  `${noSensitiveBasis.length} record(s) with special category data and no documented basis`,
        count: noSensitiveBasis.length, ids: noSensitiveBasis.map(r => r.id) });

    const noRecipient = records.filter(r =>
      r.shares_externally &&
      (!r.external_sharing_recipients || r.external_sharing_recipients.trim() === '')
    );
    if (noRecipient.length)
      items.push({ id: 'no_recipient', severity: 'amber', icon: AlertCircle,
        title: 'External sharing — recipient not recorded',
        hint:  `${noRecipient.length} record(s) with external sharing set to Yes but no recipient details`,
        count: noRecipient.length, ids: noRecipient.map(r => r.id) });

    const noLegalBasis = records.filter(r =>
      r.status === 'active' &&
      (!r.legal_basis || (Array.isArray(r.legal_basis) ? r.legal_basis.length === 0 : r.legal_basis === ''))
    );
    if (noLegalBasis.length)
      items.push({ id: 'no_legal_basis', severity: 'amber', icon: FileX,
        title: 'Active records — no legal basis selected',
        hint:  `${noLegalBasis.length} active record(s) with no lawful basis documented`,
        count: noLegalBasis.length, ids: noLegalBasis.map(r => r.id) });

    const drafts = records.filter(r => r.status === 'draft');
    if (drafts.length)
      items.push({ id: 'drafts', severity: 'neutral', icon: FileText,
        title: 'Draft records not yet submitted',
        hint:  `${drafts.length} record(s) remain in draft and have not been approved`,
        count: drafts.length, ids: drafts.map(r => r.id) });

    return items;
  }, [records]);

  // ── Breakdown data ──────────────────────────────────────────────────────────
  const breakdown = useMemo(() => {
    // 1. By department
    const deptMap = {};
    records.forEach(r => {
      const d = r.department || 'Unassigned';
      deptMap[d] = (deptMap[d] || 0) + 1;
    });
    const deptRows = Object.entries(deptMap).sort((a, b) => b[1] - a[1]);
    const deptMax  = deptRows[0]?.[1] || 1;

    // 2. Status
    const statusRows = [
      { key: 'active',         label: 'Active',         color: 'bg-emerald-500', count: stats.active         },
      { key: 'draft',          label: 'Draft',          color: 'bg-amber-400',   count: stats.draft          },
      { key: 'pending_review', label: 'Pending Review', color: 'bg-blue-500',    count: stats.pending_review },
      { key: 'approved',       label: 'Approved',       color: 'bg-purple-500',  count: stats.approved       },
      { key: 'archived',       label: 'Archived',       color: 'bg-slate-400',   count: stats.archived       },
    ];

    // 3. Personal data categories (flatten personal_data_types → category group)
    const pdCounts = {};
    records.forEach(r => {
      const cats = getCategoryNames(r.personal_data_types || [], PERSONAL_DATA_CATEGORIES);
      cats.forEach(c => { pdCounts[c] = (pdCounts[c] || 0) + 1; });
    });
    const pdChips = Object.entries(pdCounts).sort((a, b) => b[1] - a[1]);

    // 4. Sensitive data categories — stored as short codes, display with readable labels
    const SENSITIVE_TYPE_LABELS = {
      health_medical:   'Health data',
      biometric:        'Biometric data',
      genetic:          'Genetic data',
      racial_ethnic:    'Racial / Ethnic origin',
      religious:        'Religious beliefs',
      political:        'Political opinions',
      trade_union:      'Trade union membership',
      sexual:           'Sexual orientation',
      criminal:         'Criminal / disciplinary',
      psychometric:     'Psychometric data',
      children:         "Children's data",
    };
    const sdCounts = {};
    records.filter(r => r.collects_sensitive_data).forEach(r => {
      (r.sensitive_data_types || []).forEach(code => {
        const label = SENSITIVE_TYPE_LABELS[code] || code;
        sdCounts[label] = (sdCounts[label] || 0) + 1;
      });
    });
    const sdChips = Object.entries(sdCounts).sort((a, b) => b[1] - a[1]);

    // 5. Data subjects
    const dsChips = flatCount(records, 'data_subjects');

    // 6. Legal basis – personal
    const lbCounts = {};
    records.forEach(r => {
      const val = r.legal_basis;
      if (!val || (Array.isArray(val) && val.length === 0)) return;
      const bases = Array.isArray(val) ? val : [val];
      bases.forEach(b => { if (b) lbCounts[b] = (lbCounts[b] || 0) + 1; });
    });
    const lbRows   = Object.entries(lbCounts).sort((a, b) => b[1] - a[1]);
    const lbMax    = Math.max(...lbRows.map(r => r[1]), 1);
    const lbNoRecorded = records.filter(r =>
      r.status === 'active' && (!r.legal_basis || (Array.isArray(r.legal_basis) ? r.legal_basis.length === 0 : r.legal_basis === ''))
    ).length;

    // 7. Legal basis – sensitive
    const slbCounts = {};
    records.filter(r => r.collects_sensitive_data).forEach(r => {
      const v = r.sensitive_data_legal_basis;
      if (v) slbCounts[v] = (slbCounts[v] || 0) + 1;
    });
    const slbRows       = Object.entries(slbCounts).sort((a, b) => b[1] - a[1]);
    const slbMax        = Math.max(...slbRows.map(r => r[1]), 1);
    const slbNoRecorded = records.filter(r =>
      r.collects_sensitive_data && (!r.sensitive_data_legal_basis || r.sensitive_data_legal_basis === '')
    ).length;

    // 8. Electronic security
    const elCounts = {};
    records.forEach(r => {
      (r.electronic_security_measures || []).forEach(m => { if (m) elCounts[m] = (elCounts[m] || 0) + 1; });
    });
    const elRows = Object.entries(elCounts).sort((a, b) => b[1] - a[1]);

    // 9. Physical security
    const phCounts = {};
    records.forEach(r => {
      (r.paper_security_measures || []).forEach(m => { if (m) phCounts[m] = (phCounts[m] || 0) + 1; });
    });
    const phRows = Object.entries(phCounts).sort((a, b) => b[1] - a[1]);

    return { deptRows, deptMax, statusRows, pdChips, sdChips, dsChips,
             lbRows, lbMax, lbNoRecorded, slbRows, slbMax, slbNoRecorded,
             elRows, phRows };
  }, [records, stats]);

  // ── Filtered records (records view) ────────────────────────────────────────
  const filteredRecords = useMemo(() => records.filter(r => {
    const matchesSearch = !searchTerm ||
      r.process_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.purpose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.department?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus     = !selectedStatus || r.status === selectedStatus;
    const matchesStatCard   = activeStatCard === 'total' ||
      (activeStatCard === 'needs_attention' ? (r.status === 'draft' || r.status === 'pending_review') :
       activeStatCard === 'sensitive' ? r.collects_sensitive_data :
       r.status === activeStatCard);
    const matchesDepartment = !selectedDepartmentId ||
      r.department?.toLowerCase() === selectedDepartmentId.toLowerCase();
    const matchesFilterDept  = !filterDept ||
      r.department?.toLowerCase() === filterDept.toLowerCase();
    const matchesAttention   = !attentionIds || attentionIds.has(r.id);
    return matchesSearch && matchesStatus && matchesStatCard && matchesDepartment && matchesFilterDept && matchesAttention;
  }), [records, searchTerm, selectedStatus, activeStatCard, selectedDepartmentId, filterDept, attentionIds]);

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity?.entity_name || 'Unknown';
  };

  // Navigate to records view filtered by a department bar click
  const goToRecordsByDept = (deptName) => {
    setFilterDept(deptName);
    setActiveStatCard('total');
    setAttentionIds(null);
    setView('records');
  };

  // ── Exports ─────────────────────────────────────────────────────────────────
  const exportToExcel = async () => {
    if (!selectedClientId) { toast.error('Please select a client to export'); return; }
    try {
      const res = await exportAPI.ropaXlsx(selectedClientId, selectedEntityId || '');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `ropa_register_${selectedEntityId || selectedClientId}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Excel export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const exportToPDF = async () => {
    if (!selectedClientId) { toast.error('Please select a client to export'); return; }
    try {
      const res = await exportAPI.ropaPdf(selectedClientId, selectedEntityId || '');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `ropa_compliance_report_${selectedEntityId || selectedClientId}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch { toast.error('PDF export failed'); }
  };

  const exportToCSV = () => {
    const exportData = filteredRecords.map(record => ({
      'Department':             record.department || '',
      'Processing Activity':    record.process_name || '',
      'Purpose':                record.purpose?.replace(/<[^>]*>/g, '') || '',
      'Data Subjects':          (record.data_subjects || []).join('; '),
      'Legal Basis':            LEGAL_BASIS_LABELS[record.legal_basis] || record.legal_basis || '',
      'Sensitive Data':         record.collects_sensitive_data ? 'Yes' : 'No',
      'Status':                 STATUS_CONFIG[record.status]?.label || record.status,
    }));
    const escape = v => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g,'""')}"` : s;
    };
    const headers = Object.keys(exportData[0] || {});
    const csv = [headers.map(escape).join(','), ...exportData.map(row => headers.map(h => escape(row[h])).join(','))].join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), `ROPA_Export_${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('CSV export downloaded');
  };

  // ── Security bar colour by coverage ────────────────────────────────────────
  const secColor = (count) => {
    if (stats.total === 0) return 'bg-slate-300 dark:bg-slate-600';
    const pct = count / stats.total;
    if (pct >= 0.8) return 'bg-emerald-500';
    if (pct >= 0.5) return 'bg-amber-400';
    return 'bg-red-500';
  };

  // ── Completion ──────────────────────────────────────────────────────────────
  const completionPct = stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0;

  // ── Role flags ──────────────────────────────────────────────────────────────
  const showAttention  = isDPO || isClientAdmin;
  const showBreakdown  = isDPO || isClientAdmin || isEntityAdmin;

  // ════════════════════════════════════════════════════════════════════════════
  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="ropa-page">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">ROPA</h1>
            <p className="text-muted-foreground text-sm">Record of Processing Activities</p>
            {view === 'records' && (
              <button
                onClick={() => { setView('dashboard'); setFilterDept(null); setAttentionIds(null); }}
                className="flex items-center gap-1 text-sm text-primary mt-1 hover:underline"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to Dashboard
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportToExcel}><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={exportToPDF}><FileText className="h-4 w-4 mr-2" />PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={exportToCSV}><FileSpreadsheet className="h-4 w-4 mr-2" />CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!isReadOnly && (
              <Button variant="outline" size="sm" onClick={() => setIntakeDialogOpen(true)}>
                Send intake link
              </Button>
            )}
            {!isReadOnly && (
              <Button size="sm" onClick={() => navigate('/compliance/ropa/new')} data-testid="add-ropa-btn">
                <Plus className="h-4 w-4 mr-2" />+ Add record
              </Button>
            )}
          </div>
        </div>

        {/* ── Dialogs ─────────────────────────────────────────────────────────── */}
        {!isReadOnly && (
          <CreateTempGuestDialog
            open={intakeDialogOpen}
            onOpenChange={setIntakeDialogOpen}
            defaultModule="ropa_intake"
            onCreated={fetchData}
            entities={entities}
            lockedEntityId={isEntityAdmin ? user?.entity_id : null}
          />
        )}
        <CSVImportDialog open={csvImportDialogOpen} onOpenChange={setCsvImportDialogOpen} onComplete={fetchData} />

        {/* ══════════════════════════════════════════════════════════════════════
            DASHBOARD VIEW
        ══════════════════════════════════════════════════════════════════════ */}
        {view === 'dashboard' && (
          <div className="space-y-6">

            {/* ── 3 Stat cards ────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Active records */}
              <Card
                className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-emerald-500"
                onClick={() => { setActiveStatCard('active'); setFilterDept(null); setAttentionIds(null); setView('records'); }}
              >
                <CardContent className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Active records</p>
                  <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{stats.active}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">of {stats.total} total</p>
                </CardContent>
              </Card>

              {/* Needs attention */}
              <Card
                className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-amber-500"
                onClick={() => { setActiveStatCard('needs_attention'); setFilterDept(null); setAttentionIds(null); setView('records'); }}
              >
                <CardContent className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Needs attention</p>
                  <p className="text-4xl font-bold text-amber-600 dark:text-amber-400 mt-1">{stats.needs_attention}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">draft + pending review</p>
                </CardContent>
              </Card>

              {/* Sensitive data */}
              <Card
                className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-red-500"
                onClick={() => { setActiveStatCard('sensitive'); setFilterDept(null); setAttentionIds(null); setView('records'); }}
              >
                <CardContent className="p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Sensitive data records</p>
                  <p className="text-4xl font-bold text-red-600 dark:text-red-400 mt-1">{stats.sensitive}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">special category data</p>
                </CardContent>
              </Card>
            </div>

            {/* ── Completion bar ───────────────────────────────────────────────── */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-sm text-slate-800 dark:text-slate-100">ROPA completion</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {stats.active} of {stats.total} records active and approved
                    </p>
                  </div>
                  <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{completionPct}%</span>
                </div>
                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                    style={{ width: `${completionPct}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* ── Attention required panel ─────────────────────────────────────── */}
            {showAttention && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
                  Attention required
                </p>
                <Card>
                  <CardContent className="p-0">
                    {attention.length === 0 ? (
                      <div className="flex items-center gap-3 px-5 py-4 text-emerald-700 dark:text-emerald-400">
                        <ShieldCheck className="h-5 w-5 shrink-0" />
                        <span className="text-sm font-medium">All clear — no issues found in your ROPA register</span>
                      </div>
                    ) : (
                      attention.map((item, idx) => {
                        const Icon = item.icon;
                        const borderColor = {
                          red:     'border-l-red-500',
                          amber:   'border-l-amber-500',
                          neutral: 'border-l-slate-400',
                        }[item.severity] || 'border-l-slate-400';
                        const iconColor = {
                          red:     'text-red-500',
                          amber:   'text-amber-500',
                          neutral: 'text-slate-400',
                        }[item.severity] || 'text-slate-400';
                        const badgeColor = {
                          red:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                          amber:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                          neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                        }[item.severity] || '';

                        return (
                          <button
                            key={item.id}
                            className={cn(
                              'w-full flex items-center gap-4 px-5 py-4 border-l-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors',
                              borderColor,
                              idx > 0 && 'border-t border-t-border'
                            )}
                            onClick={() => {
                              setActiveStatCard('total');
                              setFilterDept(null);
                              setAttentionIds(new Set(item.ids));
                              setView('records');
                            }}
                          >
                            <Icon className={cn('h-5 w-5 shrink-0', iconColor)} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{item.title}</p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.hint}</p>
                            </div>
                            <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full shrink-0', badgeColor)}>
                              {item.count} {item.count === 1 ? 'record' : 'records'}
                            </span>
                            <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                          </button>
                        );
                      })
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ── Register breakdown ────────────────────────────────────────────── */}
            {showBreakdown && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
                  Register breakdown
                </p>
                <div className="space-y-4">

                  {/* Row 1 — Panels 1 & 2: dept bar + status bar */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                    {/* Panel 1: Activities by department */}
                    <Panel title="Activities by department">
                      {breakdown.deptRows.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No department data yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {breakdown.deptRows.map(([dept, count]) => (
                            <BarRow
                              key={dept}
                              label={dept}
                              count={count}
                              max={breakdown.deptMax}
                              color="bg-teal-500"
                              onClick={() => goToRecordsByDept(dept)}
                            />
                          ))}
                        </div>
                      )}
                    </Panel>

                    {/* Panel 2: Type of records */}
                    <Panel title="Type of records">
                      <div className="space-y-3">
                        {breakdown.statusRows.map(row => (
                          <BarRow
                            key={row.key}
                            label={row.label}
                            count={row.count}
                            max={Math.max(...breakdown.statusRows.map(r => r.count), 1)}
                            color={row.color}
                            onClick={() => {
                              setActiveStatCard(row.key);
                              setFilterDept(null);
                              setAttentionIds(null);
                              setView('records');
                            }}
                          />
                        ))}
                      </div>
                    </Panel>
                  </div>

                  {/* Panel 3: Types of personal data — full width */}
                  <Panel title="Types of personal data">
                    {breakdown.pdChips.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No personal data categories recorded yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {breakdown.pdChips.map(([cat, count]) => (
                          <Chip key={cat} label={cat} count={count} color="teal" />
                        ))}
                      </div>
                    )}
                  </Panel>

                  {/* Panel 4: Types of sensitive data — full width, only if any */}
                  {breakdown.sdChips.length > 0 && (
                    <Panel title="Types of sensitive data">
                      <div className="flex flex-wrap gap-2">
                        {breakdown.sdChips.map(([cat, count]) => (
                          <Chip key={cat} label={cat} count={count} color="red" />
                        ))}
                      </div>
                    </Panel>
                  )}

                  {/* Panel 5: Data subjects — full width */}
                  <Panel title="Data subjects">
                    {breakdown.dsChips.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No data subject types recorded yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {breakdown.dsChips.map(([subj, count]) => (
                          <Chip key={subj} label={subj} count={count} color="blue" />
                        ))}
                      </div>
                    )}
                  </Panel>

                  {/* Row 2 — Panels 6 & 7: legal basis bars */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                    {/* Panel 6: Legal basis — personal data */}
                    <Panel title="Legal basis — personal data">
                      {breakdown.lbRows.length === 0 && breakdown.lbNoRecorded === 0 ? (
                        <p className="text-sm text-muted-foreground">No legal basis data yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {breakdown.lbRows.map(([basis, count]) => (
                            <BarRow
                              key={basis}
                              label={LEGAL_BASIS_LABELS[basis] || basis}
                              count={count}
                              max={breakdown.lbMax}
                              color={LEGAL_BASIS_COLORS[basis] || 'bg-slate-400'}
                            />
                          ))}
                          {breakdown.lbNoRecorded > 0 && (
                            <BarRow
                              label="Not recorded"
                              count={breakdown.lbNoRecorded}
                              max={breakdown.lbMax}
                              color="bg-red-500"
                            />
                          )}
                        </div>
                      )}
                    </Panel>

                    {/* Panel 7: Legal basis — sensitive data */}
                    <Panel title="Legal basis — sensitive data">
                      {breakdown.slbRows.length === 0 && breakdown.slbNoRecorded === 0 ? (
                        <p className="text-sm text-muted-foreground">No sensitive data legal basis recorded.</p>
                      ) : (
                        <div className="space-y-3">
                          {breakdown.slbRows.map(([basis, count]) => (
                            <BarRow
                              key={basis}
                              label={SENSITIVE_BASIS_LABELS[basis] || basis}
                              count={count}
                              max={breakdown.slbMax}
                              color="bg-purple-500"
                            />
                          ))}
                          {breakdown.slbNoRecorded > 0 && (
                            <BarRow
                              label="Not recorded"
                              count={breakdown.slbNoRecorded}
                              max={breakdown.slbMax}
                              color="bg-red-500"
                            />
                          )}
                        </div>
                      )}
                    </Panel>
                  </div>

                  {/* Row 3 — Panels 8 & 9: security bars */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                    {/* Panel 8: Electronic security */}
                    <Panel title="Security — electronic">
                      {breakdown.elRows.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No electronic security measures recorded.</p>
                      ) : (
                        <div className="space-y-3">
                          {breakdown.elRows.map(([measure, count]) => (
                            <BarRow
                              key={measure}
                              label={measure}
                              count={count}
                              max={stats.total}
                              color={secColor(count)}
                            />
                          ))}
                        </div>
                      )}
                    </Panel>

                    {/* Panel 9: Physical security */}
                    <Panel title="Security — physical">
                      {breakdown.phRows.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No physical security measures recorded.</p>
                      ) : (
                        <div className="space-y-3">
                          {breakdown.phRows.map(([measure, count]) => (
                            <BarRow
                              key={measure}
                              label={measure}
                              count={count}
                              max={stats.total}
                              color={secColor(count)}
                            />
                          ))}
                        </div>
                      )}
                    </Panel>
                  </div>

                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            RECORDS VIEW
        ══════════════════════════════════════════════════════════════════════ */}
        {view === 'records' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-ropa-input"
                />
              </div>
              <Select value={selectedStatus || 'all'} onValueChange={(val) => setSelectedStatus(val === 'all' ? '' : val)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterDept && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    Dept: {filterDept}
                  </Badge>
                  <button
                    onClick={() => setFilterDept(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    ✕ Clear
                  </button>
                </div>
              )}
            </div>

            {/* Table */}
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Processing Activity</TableHead>
                      {isDPO && <TableHead>Entity</TableHead>}
                      <TableHead>Lawful Basis</TableHead>
                      <TableHead>Data Categories</TableHead>
                      <TableHead>Sensitive</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={isDPO ? 7 : 6} className="text-center py-8">
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                            Loading...
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : filteredRecords.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isDPO ? 7 : 6} className="p-0 border-0">
                          <div className="py-12 px-6 flex flex-col items-center justify-center text-center">
                            <div className="relative mb-6">
                              <div className="absolute inset-0 bg-teal-500/20 rounded-full blur-xl" />
                              <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-500/10 to-teal-600/10 border border-teal-500/20">
                                <ClipboardList className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                              </div>
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                              No processing activities yet
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-6">
                              Start documenting your data processing activities to maintain compliance and demonstrate accountability.
                            </p>
                            {!isReadOnly && (
                              <Button onClick={() => navigate('/compliance/ropa/new')}
                                className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                                Create First Record
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRecords.map((record) => {
                        const categoryCount = getCategoryNames(record.personal_data_types || [], PERSONAL_DATA_CATEGORIES).length;
                        const statusConfig  = STATUS_CONFIG[record.status] || STATUS_CONFIG.draft;
                        return (
                          <TableRow
                            key={record.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/compliance/ropa/view/${record.id}`)}
                            data-testid={`ropa-row-${record.id}`}
                          >
                            <TableCell>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium">{record.process_name}</p>
                                  <TasksCountBadge counts={taskCounts[record.id]} />
                                </div>
                                <p className="text-xs text-muted-foreground truncate max-w-[280px]">
                                  {record.department}{record.unit && ` / ${record.unit}`}
                                </p>
                              </div>
                            </TableCell>
                            {isDPO && <TableCell className="text-sm">{getEntityName(record.entity_id)}</TableCell>}
                            <TableCell className="text-sm">
                              {LEGAL_BASIS_LABELS[record.legal_basis] || record.legal_basis || '—'}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {categoryCount} {categoryCount === 1 ? 'category' : 'categories'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={cn('text-sm',
                                record.collects_sensitive_data ? 'text-amber-600 font-medium' : 'text-muted-foreground'
                              )}>
                                {record.collects_sensitive_data ? 'Yes' : 'No'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={cn('font-normal', statusConfig.color)}>{statusConfig.label}</Badge>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => navigate(`/compliance/ropa/view/${record.id}`)}>
                                    <Eye className="h-4 w-4 mr-2" />View Details
                                  </DropdownMenuItem>
                                  {!isReadOnly && (
                                    <>
                                      <DropdownMenuItem onClick={() => navigate(`/compliance/ropa/edit/${record.id}`)}>
                                        <Edit className="h-4 w-4 mr-2" />Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDelete(record.id)} className="text-red-600">
                                        <Trash2 className="h-4 w-4 mr-2" />Delete
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  <DropdownMenuItem onClick={async () => {
                                    try {
                                      const res = await exportAPI.ropaDocx(record.id);
                                      const url = window.URL.createObjectURL(new Blob([res.data]));
                                      const a = document.createElement('a'); a.href = url;
                                      a.download = res.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g,'') || 'ROPA_Record.docx';
                                      document.body.appendChild(a); a.click(); a.remove();
                                      window.URL.revokeObjectURL(url);
                                      toast.success('DOCX exported');
                                    } catch { toast.error('Export failed'); }
                                  }} data-testid={`export-ropa-docx-${record.id}`}>
                                    <FileDown className="h-4 w-4 mr-2" />Export DOCX
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
